import { atom } from 'jotai'
import isEqual from 'lodash/isEqual'

import { usersAtom } from './AtomUsers'

/**
 * User 命令原子集合
 *
 * 拆成多个职责单一的 Command Atom，方便按需引入：
 *
 * - userUpsertOneCommandAtom：新增 / 覆盖单个用户
 * - userUpsertManyCommandAtom：批量新增 / 覆盖多个用户
 * - userPatchOneCommandAtom：局部更新单个用户字段
 *
 * 所有更新路径内部都使用 lodash.isEqual，确保「有变化才写入」。
 */

/**
 * 新增或替换单个用户（如果完全相同则不会触发更新）
 *
 * 使用方式：
 * const upsertOne = useSetAtom(userUpsertOneCommandAtom)
 * upsertOne(user)
 */
export const userUpsertOneCommandAtom = atom(null, (_get, set, user) => {
  if (!user || !user.resourceId) {
    console.warn('[userUpsertOneCommandAtom] requires user with resourceId')
    return
  }

  const userId = user.resourceId

  set(usersAtom, (prev) => {
    const prevUser = prev[userId]

    // 如果完全相同就直接返回原对象，避免无效更新
    if (isEqual(prevUser, user)) {
      return prev
    }

    return {
      ...prev,
      [userId]: user,
    }
  })
})

/**
 * 批量新增或替换多个用户（自动跳过未变化的用户）
 *
 * 使用方式：
 * const upsertMany = useSetAtom(userUpsertManyCommandAtom)
 * upsertMany(usersArray)
 */
export const userUpsertManyCommandAtom = atom(null, (_get, set, users) => {
  if (!Array.isArray(users) || users.length === 0) {
    return
  }

  set(usersAtom, (prev) => {
    let changed = false
    const next = { ...prev }

    users.forEach((user) => {
      if (!user || !user.resourceId) return
      const userId = user.resourceId
      const prevUser = prev[userId]

      if (!isEqual(prevUser, user)) {
        next[userId] = user
        changed = true
      }
    })

    // 如果所有用户都与之前相同，则直接返回 prev，避免不必要的引用变化
    return changed ? next : prev
  })
})

/**
 * 局部更新单个用户字段
 *
 * 使用方式：
 * const patchOne = useSetAtom(userPatchOneCommandAtom)
 * patchOne({ userId, patch: { firstName: 'NewName' } })
 */
export const userPatchOneCommandAtom = atom(null, (get, set, payload) => {
  if (!payload || typeof payload !== 'object') return
  const { userId, patch } = payload

  if (!userId || !patch || typeof patch !== 'object') {
    console.warn('[userPatchOneCommandAtom] requires userId and patch object')
    return
  }

  // Do a check before call set to usersAtom
  const prevUsers = get(usersAtom)
  const prevUser = prevUsers[userId] || {}
  const newUser = { ...prevUser, ...patch }

  if (isEqual(prevUser, newUser)) {
    return
  }

  set(usersAtom, { ...prevUsers, [userId]: newUser })
})

/**
 * 重置用户列表
 */
export const userResetAllCommandAtom = atom(null, (get, set) => {
  // TODO, need to update users atom to all this
  set(usersAtom, {})
})
